import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Button,
  Tooltip,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  X,
  Reply,
  ReplyAll,
  Forward,
  Archive,
  Trash2,
  Star,
  StarOff,
  ChevronLeft,
  Printer,
  MoreVertical,
} from 'lucide-react';
import EmailDisplay from './EmailDisplay';
import ReplyComposer from '../composer/ReplyComposer';

/**
 * ThreadDisplay - Email thread/conversation view
 * Shows all messages in a thread with reply composer
 */
const ThreadDisplay = ({
  thread,
  messages = [],
  loading = false,
  onClose,
  onReply,
  onReplyAll,
  onForward,
  onStar,
  onMarkRead,
  onArchive,
  onDelete,
  onSendReply,
  onDownloadAttachment,
  userEmail = '',
}) => {
  const [replyMode, setReplyMode] = useState(null); // null | 'reply' | 'replyAll' | 'forward'
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [sending, setSending] = useState(false);

  // Get latest message
  const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;

  // Get thread subject
  const subject = thread?.subject || latestMessage?.subject || '(No subject)';

  // Handle reply actions
  const handleReply = useCallback((message) => {
    setReplyToMessage(message);
    setReplyMode('reply');
    onReply?.(message);
  }, [onReply]);

  const handleReplyAll = useCallback((message) => {
    setReplyToMessage(message);
    setReplyMode('replyAll');
    onReplyAll?.(message);
  }, [onReplyAll]);

  const handleForward = useCallback((message) => {
    setReplyToMessage(message);
    setReplyMode('forward');
    onForward?.(message);
  }, [onForward]);

  // Close reply composer
  const closeReplyComposer = useCallback(() => {
    setReplyMode(null);
    setReplyToMessage(null);
  }, []);

  // Handle send reply
  const handleSendReply = useCallback(async (data) => {
    if (!onSendReply) return;
    
    setSending(true);
    try {
      await onSendReply({
        ...data,
        mode: replyMode,
        replyToMessage,
      });
      closeReplyComposer();
    } finally {
      setSending(false);
    }
  }, [onSendReply, replyMode, replyToMessage, closeReplyComposer]);

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="40%" />
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="rectangular" height={200} />
        </Box>
      </Box>
    );
  }

  // Render empty state
  if (!thread && messages.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body1">Select an email to view</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ mr: 1 }}>
              <ChevronLeft size={20} />
            </IconButton>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subject}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {messages.length} message{messages.length !== 1 ? 's' : ''} in this conversation
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Archive">
            <IconButton size="small" onClick={() => onArchive?.(thread?.id)}>
              <Archive size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDelete?.(thread?.id)} color="error">
              <Trash2 size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton size="small" onClick={() => window.print()}>
              <Printer size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
        }}
      >
        {messages.map((message, index) => (
          <EmailDisplay
            key={message.id}
            message={message}
            isExpanded={index === messages.length - 1} // Expand latest message
            isLatest={index === messages.length - 1}
            onReply={handleReply}
            onReplyAll={handleReplyAll}
            onForward={handleForward}
            onStar={onStar}
            onMarkRead={onMarkRead}
            onArchive={onArchive}
            onDelete={onDelete}
            onDownloadAttachment={onDownloadAttachment}
            showActions={index === messages.length - 1}
          />
        ))}

        {/* Reply Composer */}
        {replyMode && replyToMessage && (
          <Box sx={{ mt: 2 }}>
            <ReplyComposer
              message={replyToMessage}
              mode={replyMode}
              userEmail={userEmail}
              onSend={handleSendReply}
              onClose={closeReplyComposer}
              sending={sending}
            />
          </Box>
        )}
      </Box>

      {/* Quick Reply Actions (when no reply composer is open) */}
      {!replyMode && latestMessage && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<Reply size={16} />}
            onClick={() => handleReply(latestMessage)}
          >
            Reply
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplyAll size={16} />}
            onClick={() => handleReplyAll(latestMessage)}
          >
            Reply All
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Forward size={16} />}
            onClick={() => handleForward(latestMessage)}
          >
            Forward
          </Button>
        </Box>
      )}
    </Box>
  );
};

ThreadDisplay.propTypes = {
  thread: PropTypes.shape({
    id: PropTypes.string,
    subject: PropTypes.string,
  }),
  messages: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onClose: PropTypes.func,
  onReply: PropTypes.func,
  onReplyAll: PropTypes.func,
  onForward: PropTypes.func,
  onStar: PropTypes.func,
  onMarkRead: PropTypes.func,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  onSendReply: PropTypes.func,
  onDownloadAttachment: PropTypes.func,
  userEmail: PropTypes.string,
};

export default ThreadDisplay;
